<template>
  <transition>
    <div class="mod-dial" @click="close">
      <div class="mod-cont page-logs-cont" @click.stop>
        <span class="close-main" @click.stop="close">
          <i class="bi bi-x-lg"></i>
        </span>
        <div class="mod-body page-logs">
          <h3 class="tab-title">
            {{ $t('PAGE_LOGS_TITLE') }}
            <span class="page-logs-page" v-if="page">{{ page.owner_name }}</span>
          </h3>

          <p class="page-logs-hash" v-if="page">
            hash_id:
            <code :title="$t('PAGE_LOGS_COPY')" @click="copyHash">{{ page.hash_id }}</code>
            <span class="page-logs-copied" v-if="copied">{{ $t('PAGE_LOGS_COPIED') }}</span>
          </p>

          <p class="page-logs-meta" v-if="!loading && !error && logs.length">
            {{ $t('PAGE_LOGS_JOB') }}: <b>{{ runJob }}</b>
            · {{ $t('PAGE_LOGS_STARTED') }}: <b>{{ formatDate(runStartedAt) }}</b>
            · {{ $t('PAGE_LOGS_RECORDS') }}: <b>{{ logs.length }}</b>
          </p>

          <p class="page-logs-state" v-if="loading">{{ $t('PAGE_LOGS_LOADING') }}</p>
          <p class="page-logs-state page-logs-error" v-else-if="error">{{ error }}</p>
          <p class="page-logs-state" v-else-if="!logs.length">{{ $t('PAGE_LOGS_EMPTY') }}</p>

          <div class="page-logs-list" v-else>
            <div
              class="page-logs-row"
              :class="`lvl-${row.level}`"
              v-for="(row, index) in logs"
              :key="row.id || index"
            >
              <span class="page-logs-time">{{ formatTime(row.created_at) }}</span>
              <span class="page-logs-level">{{ row.level }}</span>
              <span class="page-logs-stage" v-if="row.stage">{{ row.stage }}</span>
              <span class="page-logs-msg">
                {{ row.message }}
                <em class="page-logs-ctx" v-if="row.context">{{ formatContext(row.context) }}</em>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import moment from "moment";
import module from "@/models/AxiosModule";
import { INTRO_SET_PAGE_LOGS } from "../../store/actions/intro";

export default {
  name: "PageLogs",
  data() {
    return {
      logs: [],
      loading: true,
      error: "",
      copied: false,
      copiedTimer: null,
    };
  },
  computed: {
    ...mapGetters(["getPageLogsPage"]),
    page() {
      return this.getPageLogsPage;
    },
    // все записи одного запуска — метаданные берём из первой
    runJob() {
      return this.logs.length ? this.logs[0].job : "";
    },
    runStartedAt() {
      return this.logs.length ? this.logs[0].created_at : null;
    },
  },
  mounted() {
    this.load();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    clearTimeout(this.copiedTimer);
  },
  methods: {
    ...mapMutations([INTRO_SET_PAGE_LOGS]),
    close() {
      this[INTRO_SET_PAGE_LOGS](null);
    },
    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },
    async load() {
      if (!this.page) return;
      this.loading = true;
      this.error = "";
      try {
        const { data } = await module.Axios.post("get-page-logs", {
          hash_id: this.page.hash_id,
        });
        this.logs = data.logs || [];
      } catch (error) {
        const status = error.response && error.response.status;
        this.error = this.$t(status === 403 ? "PAGE_LOGS_DENIED" : "PAGE_LOGS_ERROR");
      } finally {
        this.loading = false;
      }
    },
    async copyHash() {
      const value = this.page && this.page.hash_id;
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
      } catch (error) {
        // clipboard API есть не везде — копируем через временное поле
        const input = document.createElement("textarea");
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      this.copied = true;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copied = false;
      }, 1500);
    },
    formatDate(value) {
      return value ? moment(value).format("DD.MM.YYYY HH:mm:ss") : "";
    },
    formatTime(value) {
      return value ? moment(value).format("HH:mm:ss.SSS") : "";
    },
    // context — json-колонка: приходит строкой или уже разобранным объектом
    formatContext(value) {
      if (typeof value !== "string") return JSON.stringify(value);
      try {
        return JSON.stringify(JSON.parse(value));
      } catch (error) {
        return value;
      }
    },
  },
};
</script>

<style scoped>
.page-logs-cont {
  width: 900px;
  max-width: 94vw;
}

.page-logs .tab-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.page-logs-page {
  font-size: 14px;
  font-weight: 400;
  color: #6b7a83;
}

.page-logs-hash {
  margin: 0 0 6px;
  font-size: 13px;
  color: #6b7a83;
}

.page-logs-hash code {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  color: #182228;
  background: #eef2f4;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  user-select: all;
}

.page-logs-hash code:hover {
  background: #d9f3ff;
}

.page-logs-copied {
  margin-left: 6px;
  font-size: 12px;
  color: #3c8dbc;
}

.page-logs-meta {
  margin: 0 0 10px;
  font-size: 13px;
  color: #6b7a83;
}

.page-logs-state {
  margin: 10px 0;
  font-size: 14px;
  color: #6b7a83;
}

.page-logs-error {
  color: #d9534f;
}

.page-logs-list {
  max-height: 60vh;
  overflow: auto;
  border: 1px solid #e6ebee;
  border-radius: 8px;
  background: #fbfcfd;
}

.page-logs-row {
  display: flex;
  gap: 8px;
  padding: 4px 10px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  border-bottom: 1px solid #eef2f4;
  align-items: flex-start;
}

.page-logs-row:last-child {
  border-bottom: none;
}

.page-logs-time {
  flex: 0 0 84px;
  color: #97a4ab;
}

.page-logs-level {
  flex: 0 0 46px;
  text-transform: uppercase;
  color: #6b7a83;
}

.page-logs-stage {
  flex: 0 0 auto;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #3c8dbc;
}

.page-logs-msg {
  flex: 1 1 auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: #182228;
}

.page-logs-ctx {
  display: block;
  color: #97a4ab;
  font-style: normal;
}

.page-logs-row.lvl-warn {
  background: #fff8e6;
}

.page-logs-row.lvl-warn .page-logs-level {
  color: #b8860b;
}

.page-logs-row.lvl-error {
  background: #fdeceb;
}

.page-logs-row.lvl-error .page-logs-level,
.page-logs-row.lvl-error .page-logs-msg {
  color: #c0392b;
}

.page-logs-row.lvl-debug .page-logs-msg {
  color: #6b7a83;
}
</style>
